/**
 * ReviewActionBar — Phase 07
 *
 * Action buttons for engine review: Approve, Dispute, Request Rerun, Waive.
 * Buttons are enabled/disabled based on the current review state.
 */

import React from 'react';
import { Button } from '../../ui/Button';
import type { EngineReviewState, EngineId, VerifierReport, ReviewPanelMode } from '../../../types/engineReview';
import { getActionsForState, ENGINE_NAMES } from '../../../types/engineReview';

// =============================================================================
// ICONS
// =============================================================================

const CheckIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const XIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const FlagIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 3v18 M3 4h11l-1.5 3L14 10H3"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const SkipIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 5l7 7-7 7M5 5l7 7-7 7"
    />
  </svg>
);

const PlayIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

// =============================================================================
// TOOLTIP COMPONENT (SIMPLE)
// =============================================================================

interface TooltipWrapperProps {
  tooltip?: string;
  children: React.ReactNode;
}

function TooltipWrapper({ tooltip, children }: TooltipWrapperProps) {
  if (!tooltip) {
    return <>{children}</>;
  }

  return (
    <div className="relative group inline-block">
      {children}
      <div
        role="tooltip"
        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10"
        style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-text-primary)' }}
      >
        {tooltip}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent" style={{ borderTopColor: 'var(--color-text-primary)' }} />
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export interface ReviewActionBarProps {
  status: EngineReviewState;
  engineId: EngineId;
  /** Verifier report - if has blockers, approve is disabled */
  verifierReport?: VerifierReport;
  /** Whether action is currently being submitted */
  isSubmitting?: boolean;
  /**
   * Review panel mode. Bug #9: the witness/external rerun route
   * (`/external-accountant/review/{id}/{engine}/rerun`) does not exist on the
   * backend — it was deliberately left out of scope per the Phase 07.3b plan
   * §Q3 (see `backend/app/api/v1/external_accountant.py:593`). When
   * `mode === 'external'`, "Request Rerun" is disabled with an explanatory
   * tooltip instead of firing a request that always 404s. Optional and
   * defaults to `undefined` (treated as non-external) so existing callers
   * that don't yet pass a mode keep their current behavior.
   */
  mode?: ReviewPanelMode;
  /**
   * GAP-P1-ROLECAP: role capability for this surface. Defaults to 'full'
   * (existing behavior, all actions rendered per state). 'read+dispute'
   * (attorney / adjuster) renders ONLY the Dispute button — approve /
   * start-review / rerun / waive are HIDDEN entirely, not disabled.
   * Read-only roles never mount this bar at all (the panel gates it).
   */
  capability?: 'full' | 'read+dispute';
  /** Callbacks for each action */
  onStartReview: () => void;
  onApprove: () => void;
  onDispute: () => void;
  onRerun: () => void;
  onWaive: () => void;
  /** Test ID prefix */
  testIdPrefix?: string;
}

export default function ReviewActionBar({
  status,
  engineId,
  verifierReport,
  isSubmitting = false,
  mode,
  capability = 'full',
  onStartReview,
  onApprove,
  onDispute,
  onRerun,
  onWaive,
  testIdPrefix = 'action',
}: ReviewActionBarProps) {
  const engineName = ENGINE_NAMES[engineId];
  const actions = getActionsForState(status);
  const isExternalMode = mode === 'external';
  // GAP-P1-ROLECAP: dispute-only capability hides (does not disable) every
  // non-dispute action.
  const showFullActions = capability === 'full';

  // Check for verifier blockers
  const hasBlockers = (verifierReport?.checks_failed ?? []).some(
    (check) => check.severity === 'blocker',
  );
  const hasWarnings = (verifierReport?.checks_failed ?? []).some(
    (check) => check.severity === 'warning',
  );

  // Override approve if there are blockers
  let approveEnabled = actions.approve && !hasBlockers;
  let approveTooltip = actions.approveTooltip;
  if (hasBlockers) {
    approveEnabled = false;
    approveTooltip = 'Cannot approve — verifier found blocking issues';
  }

  return (
    <div
      className="flex flex-wrap items-center gap-3 py-3 px-4 border-t rounded-b-lg"
      style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
      role="group"
      aria-label={`Actions for ${engineName} engine`}
      data-testid={`${testIdPrefix}-bar`}
    >
      {/* Start review (only when unreviewed) */}
      {showFullActions && actions.start_review && (
        <Button
          variant="default"
          size="sm"
          onClick={onStartReview}
          disabled={isSubmitting}
          aria-label={`Start review of ${engineName} engine`}
          data-testid={`${testIdPrefix}-start-review`}
        >
          <PlayIcon />
          Start review
        </Button>
      )}

      {/* Approve — hidden entirely for dispute-only capability */}
      {showFullActions && (
        <TooltipWrapper tooltip={!approveEnabled ? approveTooltip : undefined}>
          <Button
            variant={approveEnabled ? 'default' : 'outline'}
            size="sm"
            onClick={onApprove}
            disabled={!approveEnabled || isSubmitting}
            aria-label={`Approve ${engineName} engine`}
            data-testid={`${testIdPrefix}-approve`}
          >
            <CheckIcon />
            Approve
            {hasWarnings && approveEnabled && (
              <span className="ml-1" style={{ color: 'var(--color-needs-human-text)' }}>(with warnings)</span>
            )}
          </Button>
        </TooltipWrapper>
      )}

      {/* Dispute */}
      <TooltipWrapper tooltip={!actions.dispute ? actions.disputeTooltip : undefined}>
        <Button
          variant="outline"
          size="sm"
          onClick={onDispute}
          disabled={!actions.dispute || isSubmitting}
          aria-label={`Dispute ${engineName} engine`}
          data-testid={`${testIdPrefix}-dispute`}
          style={
            actions.dispute
              ? { color: 'var(--color-blocker)', borderColor: 'var(--color-blocker)' }
              : undefined
          }
        >
          <FlagIcon />
          Dispute
        </Button>
      </TooltipWrapper>

      {/* Request Rerun — disabled in external (witness) mode: that backend
          route doesn't exist and always 404s (see `mode` prop doc above).
          Hidden entirely for dispute-only capability. */}
      {showFullActions && (
        <TooltipWrapper
          tooltip={
            isExternalMode
              ? "Rerun requests aren't available in external review — contact your in-house team"
              : !actions.rerun
                ? actions.rerunTooltip
                : undefined
          }
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onRerun}
            disabled={isExternalMode || !actions.rerun || isSubmitting}
            aria-label={`Request rerun of ${engineName} engine`}
            data-testid={`${testIdPrefix}-rerun`}
          >
            <RefreshIcon />
            Request Rerun
          </Button>
        </TooltipWrapper>
      )}

      {/* Waive — hidden entirely for dispute-only capability */}
      {showFullActions && (
        <TooltipWrapper tooltip={!actions.waive ? actions.waiveTooltip : undefined}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onWaive}
            disabled={!actions.waive || isSubmitting}
            aria-label={`Waive ${engineName} engine`}
            data-testid={`${testIdPrefix}-waive`}
          >
            <SkipIcon />
            Waive
          </Button>
        </TooltipWrapper>
      )}

      {/* Submitting indicator */}
      {isSubmitting && (
        <span className="ml-auto text-sm flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
          <svg
            className="animate-spin h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Submitting...
        </span>
      )}
    </div>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export { ReviewActionBar };
